'use client'

import { User, X } from 'lucide-react'
import { useMemo, useState } from 'react'

import {
  Button,
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandLoading,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Skeleton,
  UserAvatar,
} from '@/components/ui'
import { cn } from '@/lib/cn'
import type { Option } from '@/types/option'

// import type { Responsible } from './workflowForm'

export type Responsible = {
  id: string
  name: string
  avatar?: string | null
  area?: string | null
}

export type AddResponsibleItem = Option & {
  avatar: string | null
  area: string | null
}

type ComboboxWorkflowResponsibleProps = {
  search: string
  handleSearch: (search: string) => void
  items: AddResponsibleItem[]
  selected?: Responsible | null
  handleSelect: (item: AddResponsibleItem) => void
  handleRemove: () => void
  searchPlaceholder?: string
  emptyMessage?: string
  loading?: boolean
  disabled?: boolean
}

const ComboboxWorkflowResponsible = ({
  search,
  handleSearch,
  items,
  selected,
  handleSelect,
  searchPlaceholder,
  emptyMessage,
  loading = false,
  disabled = false,
  handleRemove,
}: ComboboxWorkflowResponsibleProps) => {
  const [open, setOpen] = useState(false)
  const filteredItems = useMemo(
    () =>
      items.filter((item) =>
        item.label.toLowerCase().includes(search.toLowerCase()),
      ),
    [items, search],
  )

  return (
    <Popover
      open={open && !disabled}
      onOpenChange={(newOpen) => !disabled && setOpen(newOpen)}
    >
      <PopoverTrigger asChild>
        {selected ? (
          <div className="relative w-full">
            <Button
              variant="secondary"
              disabled={disabled}
              className={cn(
                'w-full justify-start text-left gap-2 border-system-border-primary bg-transparent hover:bg-white font-normal text-system-text-primary pr-8',
                !selected && 'text-system-text-secondary',
              )}
              type="button"
            >
              <UserAvatar
                src={selected.avatar ?? ''}
                name={selected.name}
                className="size-8"
              />
            </Button>
            {handleRemove && (
              <Button
                variant="ghost"
                size="sm"
                className="absolute right-1 top-1/2 size-6 -translate-y-1/2 !bg-transparent p-0 hover:text-system-state-error"
                onClick={(e) => {
                  e.stopPropagation()
                  handleRemove()
                }}
                type="button"
                disabled={disabled}
              >
                <X className="size-3" />
              </Button>
            )}
          </div>
        ) : (
          <button
            disabled={disabled}
            className={cn(
              'flex size-10 items-center justify-center gap-2 rounded-lg border border-system-border-primary bg-transparent font-normal text-system-text-primary hover:bg-system-background-base transition-colors',
              !selected && 'text-system-text-secondary',
            )}
            type="button"
          >
            <User className="size-4 text-system-primary-icon" />
          </button>
        )}
      </PopoverTrigger>
      <PopoverContent className="mr-4 max-h-[--radix-popover-content-available-height] w-[--radix-popover-trigger-width] min-w-60 p-0">
        <Command shouldFilter={false}>
          <CommandInput
            value={search}
            onValueChange={(value) => handleSearch(value)}
            placeholder={searchPlaceholder ?? 'Buscar'}
          />
          <CommandList>
            {!loading && !items.length && (
              <CommandEmpty>
                {emptyMessage ?? 'Nenhum registro encontrado'}
              </CommandEmpty>
            )}

            {loading ? (
              <CommandLoading>
                <div className="flex flex-col gap-2">
                  <Skeleton className="h-9 w-full" />
                  <Skeleton className="h-9 w-full" />
                </div>
              </CommandLoading>
            ) : (
              <CommandGroup>
                {filteredItems.map((item) => (
                  <CommandItem
                    key={item.value}
                    value={item.value}
                    onSelect={(currentValue: string) => {
                      const selectedItem = items.find(
                        ({ value }) => value === currentValue,
                      )
                      if (selectedItem)
                        handleSelect({
                          value: selectedItem.value,
                          label: selectedItem.label,
                          avatar: selectedItem.avatar,
                          area: selectedItem.area,
                        })
                      setOpen(false)
                    }}
                    className="flex cursor-pointer flex-col items-start gap-2"
                  >
                    {/* <Check
                      className={cn(
                        'mr-2 size-4',
                        selected?.value === item.value
                          ? 'opacity-100'
                          : 'opacity-0',
                      )}
                    /> */}
                    {item.label}
                    {item.area && (
                      <span className="text-xs text-system-text-secondary">
                        {item.area}
                      </span>
                    )}
                  </CommandItem>
                ))}
              </CommandGroup>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}
ComboboxWorkflowResponsible.displayName = 'ComboboxWorkflowResponsible'

export { ComboboxWorkflowResponsible, type ComboboxWorkflowResponsibleProps }
